/**
 * Pure decision logic for Gmail token storage deduplication.
 *
 * Used by AuthContext.onAuthStateChange to decide whether a SIGNED_IN event
 * should trigger a store-gmail-tokens call. Extracted so the logic is testable
 * without rendering the full AuthProvider.
 */

interface AppMetadataLike {
  provider?: string;
  providers?: string[];
}

interface SessionLike {
  user?: {
    id: string;
    app_metadata?: AppMetadataLike;
  } | null;
  provider_token?: string | null;
  provider_refresh_token?: string | null;
  expires_at?: number;
}

interface StoreDecision {
  store: boolean;
  dedupeKey: string | null;
}

export function hasGoogleProvider(appMetadata: AppMetadataLike | null | undefined): boolean {
  return appMetadata?.provider === 'google'
    || appMetadata?.providers?.includes('google') === true;
}

export function isGoogleOAuthSession(session: SessionLike | null): boolean {
  if (!session?.user) {
    return false;
  }

  return session.user.app_metadata?.provider === 'google'
    || (Boolean(session.provider_token) && hasGoogleProvider(session.user.app_metadata));
}

export function shouldStoreGmailTokens(
  event: string,
  session: SessionLike | null,
  alreadyStored: Set<string>
): StoreDecision {
  // Only act on SIGNED_IN
  if (event !== 'SIGNED_IN') {
    return { store: false, dedupeKey: null };
  }

  // app_metadata.provider is the first signup provider. Linked identities are
  // represented in app_metadata.providers, so email-first Google users must
  // be accepted here too.
  if (!session?.user?.id || !hasGoogleProvider(session.user.app_metadata)) {
    return { store: false, dedupeKey: null };
  }

  // Must have a provider token to store
  if (!session.provider_token) {
    return { store: false, dedupeKey: null };
  }

  // Deduplicate by user + session expiry
  const dedupeKey = `${session.user.id}-${session.expires_at}`;

  if (alreadyStored.has(dedupeKey)) {
    return { store: false, dedupeKey };
  }

  return { store: true, dedupeKey };
}
